import { getSimilarCompanyProducts } from "src/services/invoice-api";

// M10-16358: a stockist linked to multiple companies can upload company A's invoice under
// company B. Its saved stockist-product mapping then auto-maps rows to company A's item codes,
// so they look mapped (green tick / "Items Added") even though the product isn't in company B.
// These helpers find mapped rows whose item_code doesn't exist in the invoice's company.

const extractProducts = (res: any): any[] => {
  const body = res?.data?.data ?? res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.products)) return body.products;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.rows)) return body.rows;
  return [];
};

// Mapped to a real product — unknown rows and the "OTHERS" escalation code have nothing to verify.
const isVerifiableRow = (row: any) => row?.is_item_unknown === false && !!row?.item_code && row?.item_code !== "OTHERS";

// Product-to-company membership doesn't change while the user works, so cache each answer for the
// session — re-opening an invoice (or another invoice with the same products) skips the API call.
const companyProductCache = new Map<string, boolean>();

// Large invoices can have 50+ distinct products — cap parallel calls so we don't flood the backend.
const MAX_PARALLEL_CHECKS = 6;

const isItemCodeInCompany = async (itemCode: string, companyCode: string): Promise<boolean> => {
  const cacheKey = `${companyCode}|${itemCode}`;
  const cached = companyProductCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const res = await getSimilarCompanyProducts(companyCode, "", itemCode, 1, 10);
  const exists = extractProducts(res).some((product: any) => String(product?.item_code ?? "") === itemCode);
  companyProductCache.set(cacheKey, exists);
  return exists;
};

export const findItemCodesNotInCompany = async (rows: any[], companyCode: string | number): Promise<Set<string>> => {
  const company = String(companyCode ?? "");
  const pending = Array.from(new Set<string>((rows || []).filter(isVerifiableRow).map((row: any) => String(row.item_code))));
  const notInCompany = new Set<string>();
  const worker = async () => {
    while (pending.length > 0) {
      const itemCode = pending.shift() as string;
      if (!(await isItemCodeInCompany(itemCode, company))) notInCompany.add(itemCode);
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_CHECKS, pending.length) }, worker));
  return notInCompany;
};

export const markItemCodesUnknown = (rows: any[], itemCodes: Set<string>) =>
  (rows || []).map((row: any) =>
    isVerifiableRow(row) && itemCodes.has(String(row.item_code)) ? { ...row, is_item_unknown: true, is_company_selected: false } : row
  );

export const productNamesForItemCodes = (rows: any[], itemCodes: Set<string>) =>
  Array.from(
    new Set(
      (rows || [])
        .filter((row: any) => itemCodes.has(String(row?.item_code)))
        .map((row: any) => row?.item_description)
        .filter(Boolean)
    )
  ).join(", ");
